/**
 * PresetMenu + PresetThumbnail — dashboard-layout preset picker.
 *
 * A compact "Presets" dropdown that lets authors seed a canvas with
 * pre-built or custom layouts. Renders a mini 40×20 CSS grid preview
 * for each option so authors know what they're getting before they
 * commit.
 *
 * Extracted from EmbedContentViewerPage.js in the iter-221 refactor.
 * Design goals of the extraction:
 *   • Zero behavior change from the pre-extraction version — the
 *     click-outside handler, the "replaces all current widgets"
 *     confirm dialog, and the "Save current layout as preset…" flow
 *     all move over unchanged.
 *   • Presets list is now a `presets` prop (previously a
 *     module-local `LAYOUT_PRESETS` constant) so this component is
 *     genuinely reusable across contexts (dashboards, reports,
 *     future kpi_monitor).
 */
import React from 'react';
import { LayoutDashboard, Save } from 'lucide-react';

export function PresetThumbnail({ widgets }) {
  const list = Array.isArray(widgets) ? widgets : [];
  const maxY = list.reduce((m, w) => Math.max(m, (w.y || 0) + (w.h || 3)), 0) || 1;
  const rows = Math.max(6, maxY);
  const tone = (t) => {
    if (t === 'kpi') return 'bg-indigo-400/70';
    if (t === 'bar_chart' || t === 'line_chart') return 'bg-emerald-400/70';
    if (t === 'table') return 'bg-amber-400/70';
    return 'bg-slate-400/70';
  };
  return (
    <div
      className="relative w-16 h-8 shrink-0 rounded border border-border/40 bg-background/60 overflow-hidden"
      data-testid="embed-viewer-preset-thumb"
      aria-hidden="true"
    >
      {list.map((w, i) => (
        <div
          key={i}
          className={`absolute rounded-[1px] ${tone(w.widget_type)}`}
          style={{
            left: `${((w.x || 0) / 12) * 100}%`,
            top: `${((w.y || 0) / rows) * 100}%`,
            width: `${((w.w || 4) / 12) * 100}%`,
            height: `${((w.h || 3) / rows) * 100}%`,
          }}
        />
      ))}
    </div>
  );
}

export function PresetMenu({ presets = [], onApplyPreset, onSaveAsPreset, hasWidgets, customPresets = [] }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const pick = (kind, id) => {
    if (hasWidgets) {
      const ok = window.confirm('Applying a preset replaces all current widgets. Continue?');
      if (!ok) return;
    }
    onApplyPreset(kind, id);
    setOpen(false);
  };
  const save = async () => {
    setOpen(false);
    const label = window.prompt('Save current layout as a workspace preset — give it a name:');
    if (!label) return;
    onSaveAsPreset?.(label.trim());
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-border/50 px-2 py-1 text-[11px] hover:bg-muted/40"
        data-testid="embed-viewer-presets-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <LayoutDashboard className="w-3 h-3" /> Presets
        {customPresets.length > 0 && (
          <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-1 py-[1px] text-[9px]">
            +{customPresets.length}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-72 rounded-md border border-border/50 bg-background shadow-lg z-20 p-1 max-h-[70vh] overflow-y-auto"
          data-testid="embed-viewer-presets-menu"
          role="menu"
        >
          {customPresets.length > 0 && (
            <>
              <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-wider text-emerald-300/80">
                Custom
              </div>
              {customPresets.map(p => (
                <button
                  key={p.preset_id}
                  type="button"
                  onClick={() => pick('custom', p.preset_id)}
                  className="w-full text-left rounded px-2 py-1.5 hover:bg-muted/30 focus:bg-muted/40 outline-none flex items-center gap-2"
                  data-testid={`embed-viewer-preset-custom-${p.preset_id}`}
                  role="menuitem"
                >
                  <PresetThumbnail widgets={p.widgets} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{p.label}</div>
                    {p.description && (
                      <div className="text-[10px] text-muted-foreground line-clamp-2">{p.description}</div>
                    )}
                  </div>
                </button>
              ))}
              <div className="px-2 pt-2 pb-0.5 text-[9px] uppercase tracking-wider text-muted-foreground border-t border-border/30 mt-1">
                Built-in
              </div>
            </>
          )}
          {presets.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick('builtin', p.id)}
              className="w-full text-left rounded px-2 py-1.5 hover:bg-muted/30 focus:bg-muted/40 outline-none flex items-center gap-2"
              data-testid={`embed-viewer-preset-${p.id}`}
              role="menuitem"
            >
              <PresetThumbnail widgets={p.build()} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium">{p.label}</div>
                <div className="text-[10px] text-muted-foreground">{p.description}</div>
              </div>
            </button>
          ))}
          {onSaveAsPreset && hasWidgets && (
            <>
              <div className="border-t border-border/30 my-1" />
              <button
                type="button"
                onClick={save}
                className="w-full text-left rounded px-2 py-1.5 hover:bg-emerald-500/20 focus:bg-emerald-500/30 outline-none text-emerald-300 inline-flex items-center gap-1 text-xs"
                data-testid="embed-viewer-preset-save-btn"
                role="menuitem"
              >
                <Save className="w-3 h-3" /> Save current layout as preset…
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PresetMenu;
